import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useTrackAgentNextActionPillClicked } from '@air/analytics';
import { PaintbrushVerticalIcon, XIcon } from '@air/design-icons';

import { WorkspaceAgentGuidedVariantChoices } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantChoices';
import { WorkspaceAgentGuidedVariantSaveLocation } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSaveLocation';
import { WorkspaceAgentGuidedVariantSettingsContainer } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsContainer';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import {
  doesGuidedVariantEditHandleVideos,
  GUIDED_VARIANT_EDIT_OPTIONS,
  type GuidedVariantEditId,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import { WorkspaceAgentBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/bubble/WorkspaceAgentBubble';
import { WorkspaceAgentHeroUserPromptBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/WorkspaceAgentHeroUserPromptBubble';
import { WorkspaceAgentNextActions } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/next-actions/WorkspaceAgentNextActions';
import { buildWorkspaceAgentGuidedVariantSkippedLabel } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentGuidedVariantSkippedLabel';
import { type WorkspaceAgentGuidedVariantSkipCounts } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentGuidedVariantSkipCounts';
import {
  resolveWorkspaceAgentGuidedVariantOutput,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantChosenBoard,
  type WorkspaceAgentGuidedVariantSaveLocationOptions,
  type WorkspaceAgentGuidedVariantSaveOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

type GuidedVariantFlowStep = 'canceled' | 'choices' | 'needs-selection' | 'save-location' | 'settings';

type GuidedVariantFlowInitialStepParams = {
  hasSelection: boolean;
  initialEdit: GuidedVariantEditId | null;
};

const getGuidedVariantFlowInitialStep = ({
  hasSelection,
  initialEdit,
}: GuidedVariantFlowInitialStepParams): GuidedVariantFlowStep => {
  if (!hasSelection) {
    return 'needs-selection';
  }

  if (initialEdit) {
    return 'settings';
  }

  return 'choices';
};

const doesGuidedVariantFlowStepLockComposer = (step: GuidedVariantFlowStep): boolean => {
  switch (step) {
    case 'choices':
    case 'save-location':
    case 'settings':
      return true;
    case 'canceled':
    case 'needs-selection':
      return false;
    default: {
      const exhaustive: never = step;

      return exhaustive;
    }
  }
};

export type WorkspaceAgentGuidedVariantSelectBoardMenuRenderProps = {
  onSelectBoard: (board: WorkspaceAgentGuidedVariantChosenBoard) => void;
};

export type WorkspaceAgentGuidedVariantSettingsRenderer = (
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void,
) => ReactNode;

export type WorkspaceAgentGuidedVariantFlowProps = {
  canceledBoardChip?: ReactNode;
  hasSelection: boolean;
  /** `null` opens the chooser. An edit id skips it once assets are selected. */
  initialEdit: GuidedVariantEditId | null;
  onCanceledRestart: () => void;
  onClose: () => void;
  onDiscardSave: () => void;
  onEditSelected: (editId: GuidedVariantEditId) => void;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => boolean | undefined;
  onRename: () => void;
  /**
   * Fired whenever the chooser, edit settings, or save-location step is showing, so the composer
   * can lock. The select-assets prompt and canceled note stay unlocked.
   */
  onRestart: () => void;
  onSelectBoardFromMenu: (
    board: WorkspaceAgentGuidedVariantChosenBoard,
    output: WorkspaceAgentGuidedVariantOutput,
    editId: GuidedVariantEditId,
  ) => void;
  onSelectSaveLocation: (
    optionId: WorkspaceAgentGuidedVariantSaveOptionId,
    output: WorkspaceAgentGuidedVariantOutput,
    editId: GuidedVariantEditId,
  ) => void;
  /**
   * Fired after the first paint when the visible step or selected edit changes, so the host can
   * pin the chat. The host pins the open itself.
   */
  onStepChange: () => void;
  onTag: () => void;
  question: string;
  renderSelectBoardMenu: (props: WorkspaceAgentGuidedVariantSelectBoardMenuRenderProps) => ReactNode;
  saveLocations: WorkspaceAgentGuidedVariantSaveLocationOptions;
  settings: Record<GuidedVariantEditId, WorkspaceAgentGuidedVariantSettingsRenderer>;
  skipCounts: WorkspaceAgentGuidedVariantSkipCounts;
};

export const WorkspaceAgentGuidedVariantFlow = memo(
  ({
    canceledBoardChip,
    hasSelection,
    initialEdit,
    onCanceledRestart,
    onClose,
    onDiscardSave,
    onEditSelected,
    onGenerate,
    onRename,
    onRestart,
    onSelectBoardFromMenu,
    onSelectSaveLocation,
    onStepChange,
    onTag,
    question,
    renderSelectBoardMenu,
    saveLocations,
    settings,
    skipCounts,
  }: WorkspaceAgentGuidedVariantFlowProps) => {
    const { trackAgentNextActionPillClicked } = useTrackAgentNextActionPillClicked();
    const didNotifyStepChangeRef = useRef<boolean>();
    const initialEditToTrackRef = useRef<GuidedVariantEditId | null>(hasSelection && initialEdit ? initialEdit : null);
    const [step, setStep] = useState<GuidedVariantFlowStep>(() =>
      getGuidedVariantFlowInitialStep({ hasSelection, initialEdit }),
    );
    const [edit, setEdit] = useState<GuidedVariantEditId | null>(hasSelection && initialEdit ? initialEdit : null);
    const [allowsJpeg, setAllowsJpeg] = useState(true);
    const [output, setOutput] = useState<WorkspaceAgentGuidedVariantOutput>(
      WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
    );

    useIsomorphicLayoutEffect(() => {
      const editId = initialEditToTrackRef.current;

      if (!editId) {
        return;
      }

      initialEditToTrackRef.current = null;
      onEditSelected(editId);
    }, [onEditSelected]);

    useIsomorphicLayoutEffect(() => {
      if (!didNotifyStepChangeRef.current) {
        didNotifyStepChangeRef.current = true;
        return;
      }

      onStepChange();
    }, [edit, onStepChange, step]);

    useIsomorphicLayoutEffect(() => {
      if (!doesGuidedVariantFlowStepLockComposer(step)) {
        return;
      }

      onRestart();
    }, [onRestart, step]);
    const selectedOption = GUIDED_VARIANT_EDIT_OPTIONS.find((option) => option.id === edit);
    const includesVideos = edit !== null && doesGuidedVariantEditHandleVideos(edit);
    const allowsFormat = !includesVideos || skipCounts.skippedVideoCount < 1;
    const skippedLabel =
      step === 'settings' && selectedOption
        ? buildWorkspaceAgentGuidedVariantSkippedLabel({
            ...skipCounts,
            includesVideos,
            toolName: selectedOption.label,
          })
        : null;

    const handleSelect = useCallback(
      (optionId: GuidedVariantEditId) => {
        onEditSelected(optionId);
        setEdit(optionId);
        setStep('settings');
      },
      [onEditSelected],
    );

    const handleEditChange = useCallback(
      (optionId: GuidedVariantEditId) => {
        onEditSelected(optionId);
        setEdit(optionId);
      },
      [onEditSelected],
    );

    const handleCancel = useCallback(() => {
      setAllowsJpeg(true);
      setOutput(WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT);
      setStep('canceled');
      onDiscardSave();
    }, [onDiscardSave]);

    const handleMakeVariantsAgain = useCallback(() => {
      setAllowsJpeg(true);
      onCanceledRestart();
      trackAgentNextActionPillClicked({ pill_id: 'make_variants' });
      setEdit(null);
      setOutput(WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT);
      setStep('choices');
    }, [onCanceledRestart, trackAgentNextActionPillClicked]);

    const handleRename = useCallback(() => {
      trackAgentNextActionPillClicked({ pill_id: 'rename' });
      onRename();
    }, [onRename, trackAgentNextActionPillClicked]);

    const handleTag = useCallback(() => {
      trackAgentNextActionPillClicked({ pill_id: 'tag' });
      onTag();
    }, [onTag, trackAgentNextActionPillClicked]);

    useEffect(() => {
      setOutput((current) => resolveWorkspaceAgentGuidedVariantOutput(current, { allowsFormat, allowsJpeg }));
    }, [allowsFormat, allowsJpeg]);

    useIsomorphicLayoutEffect(() => {
      if (step !== 'needs-selection' || !hasSelection) {
        return;
      }

      if (initialEdit) {
        onEditSelected(initialEdit);
      }

      setEdit(initialEdit);
      setStep(getGuidedVariantFlowInitialStep({ hasSelection: true, initialEdit }));
    }, [hasSelection, initialEdit, onEditSelected, step]);

    useEffect(() => {
      if (hasSelection || step === 'needs-selection') {
        return;
      }

      onClose();
    }, [hasSelection, onClose, step]);

    const handleGenerate = useCallback(
      (request: WorkspaceAgentGuidedVariantGenerateRequest) => {
        if (onGenerate(request) === false) {
          return;
        }

        setAllowsJpeg(request.allowsJpeg);
        setOutput((current) =>
          resolveWorkspaceAgentGuidedVariantOutput(current, {
            allowsFormat,
            allowsJpeg: request.allowsJpeg,
          }),
        );
        setStep('save-location');
      },
      [allowsFormat, onGenerate],
    );

    const handleOutputChange = useCallback((nextOutput: WorkspaceAgentGuidedVariantOutput) => {
      setOutput(nextOutput);
    }, []);

    const resolvedOutput = resolveWorkspaceAgentGuidedVariantOutput(output, { allowsFormat, allowsJpeg });

    const handleSelectSaveOption = useCallback(
      (optionId: WorkspaceAgentGuidedVariantSaveOptionId) => {
        if (!edit) {
          return;
        }

        onSelectSaveLocation(optionId, resolvedOutput, edit);
      },
      [edit, onSelectSaveLocation, resolvedOutput],
    );

    const handleSelectBoardFromMenu = useCallback(
      (board: WorkspaceAgentGuidedVariantChosenBoard) => {
        if (!edit) {
          return;
        }

        onSelectBoardFromMenu(board, resolvedOutput, edit);
      },
      [edit, onSelectBoardFromMenu, resolvedOutput],
    );

    return (
      <div className="flex w-full flex-col gap-3">
        <WorkspaceAgentBubble data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_USER_MESSAGE" variant="userHero">
          <WorkspaceAgentHeroUserPromptBubble
            description="Crop, resize, remove background, and more"
            icon={PaintbrushVerticalIcon}
            title="Make edits and variants"
          />
        </WorkspaceAgentBubble>

        {step === 'needs-selection' ? (
          <p
            className="ads:text-primary text-14 text-gray-12 leading-5"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_ASSETS"
          >
            Select assets on the board first — Air needs something in scope before starting a task.
          </p>
        ) : null}

        {step === 'save-location' ? (
          <WorkspaceAgentGuidedVariantSaveLocation
            allowsFormat={allowsFormat}
            allowsJpeg={allowsJpeg}
            onDiscard={handleCancel}
            onOutputChange={handleOutputChange}
            onSelect={handleSelectSaveOption}
            options={saveLocations}
            output={output}
            selectBoardMenu={renderSelectBoardMenu({ onSelectBoard: handleSelectBoardFromMenu })}
          />
        ) : null}

        {step === 'canceled' ? (
          <>
            <span
              className="inline-flex items-center gap-1 self-end rounded-full ads:bg-subtle bg-gray-3 px-2 py-1 ads:text-primary text-12 text-gray-12"
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CANCELED_CHIP"
            >
              <XIcon className="size-3 ads:text-icon-emphasis" />
              Canceled
            </span>
            <div
              className="ads:text-primary text-14 text-gray-12 leading-5"
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CANCELED_BODY"
            >
              <p>
                I didn&apos;t apply anything — we were still lining up the details.
                {canceledBoardChip ? <> Files in {canceledBoardChip} are unchanged.</> : ' Files are unchanged.'}
              </p>
              <p className="mt-3">What change were you expecting to see, or should we switch to a different task?</p>
            </div>
            <div className="pt-2">
              <WorkspaceAgentNextActions
                onMakeVariants={handleMakeVariantsAgain}
                onRename={handleRename}
                onTag={handleTag}
              />
            </div>
          </>
        ) : null}

        {step === 'choices' || step === 'settings' ? (
          <div className="flex flex-col gap-1">
            <p className="text-14 text-gray-12" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_QUESTION">
              {question}
            </p>
            {skippedLabel ? (
              <p className="text-14 text-gray-9" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SKIPPED">
                {skippedLabel}
              </p>
            ) : null}
          </div>
        ) : null}

        {step === 'choices' ? (
          <WorkspaceAgentGuidedVariantChoices
            onCancel={handleCancel}
            onSelect={handleSelect}
            options={GUIDED_VARIANT_EDIT_OPTIONS}
          />
        ) : null}

        {step === 'settings' && edit ? (
          <WorkspaceAgentGuidedVariantSettingsContainer
            onClose={handleCancel}
            onValueChange={handleEditChange}
            options={GUIDED_VARIANT_EDIT_OPTIONS}
            value={edit}
          >
            {settings[edit](handleGenerate)}
          </WorkspaceAgentGuidedVariantSettingsContainer>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantFlow.displayName = 'WorkspaceAgentGuidedVariantFlow';
